import { ObjectId, session, http, uiRuntimeConfig, metadata } from 'lib_ui-services';
import * as appearance from 'lib_ui-services/src/appearance';
import logging from '@sstdev/lib_logging';
import connector from './connector';
import { readerAddress } from '@sstdev/tool_reader-provisioning/src/readerAddress';

const STORAGE_KEY = 'readerSetup';
export const _private = {
    http,
    darkMode: appearance.getDarkMode,
    brand: () => uiRuntimeConfig.getBrand(),
    storage: () => globalThis.sessionStorage,
    forgetHelper: () => connector.forget(),
    cleanupTimeoutMs: 3000
};
let current;
function saved() {
    try {
        return JSON.parse(_private.storage()?.getItem(STORAGE_KEY) || 'null');
    } catch {
        return null;
    }
}
function forget() {
    try {
        _private.storage()?.removeItem(STORAGE_KEY);
    } catch {
        // Setup still works when browser storage is unavailable.
    }
}
export function clear() {
    current = undefined;
    forget();
}
export function isCurrent(setup) {
    return current === setup;
}
// Retain only a session reference and readiness flag in this tab. Reader inputs,
// pairing tokens and passwords are never written to browser storage.
export function remember(setup) {
    if (!isCurrent(setup)) return;
    if (!setup.id) return forget();
    const { id, scope, userId, base, mqttLoginReady } = setup;
    try {
        _private.storage()?.setItem(STORAGE_KEY, JSON.stringify({ id, scope, userId, base, mqttLoginReady }));
    } catch {
        // Refresh recovery is optional when browser storage is unavailable.
    }
}
export async function endSession() {
    const setup = current || saved();
    clear(); // Stop pending form actions before logging out or changing tenants.
    // In the Android app the helper runs in-process; drop its passwords with the login.
    await _private.forgetHelper();
    if (!setup?.id) return;
    let timer;
    try {
        // Finish while the old tenant and login are still available. Offline
        // cleanup must not trap a user on logout; server expiry is the fallback.
        await Promise.race([
            _private.http.remove(`${setup.base}/${setup.id}`).catch(error => {
                // A simultaneous helper report or theme change may update the revision.
                if (error.status !== 409) throw error;
                return _private.http.remove(`${setup.base}/${setup.id}`);
            }),
            new Promise((_, reject) => {
                timer = setTimeout(
                    () => reject(new Error('Reader setup cleanup timed out')),
                    _private.cleanupTimeoutMs
                );
            })
        ]);
    } catch (error) {
        if (error.status !== 404) logging.warn('Unable to end reader setup; it will expire automatically.');
    } finally {
        clearTimeout(timer);
    }
}
export function launchAppearance() {
    return {
        darkMode: _private.darkMode(),
        brand: _private.brand()?.toLowerCase().includes('redbeam') ? 'redbeam' : 'sstid'
    };
}
export function syncAppearance(setup = current) {
    const { darkMode, brand } = launchAppearance();
    const useCaseName = setup?.useCaseName;
    if (!setup?.id || !isCurrent(setup) || typeof darkMode !== 'boolean') return;
    // Serialize changes so a slow response cannot restore the previous mode.
    setup.appearanceUpdate = (setup.appearanceUpdate || Promise.resolve()).then(async () => {
        if (
            !isCurrent(setup) ||
            (setup.lastDarkMode === darkMode && setup.lastUseCaseName === useCaseName && setup.lastBrand === brand)
        )
            return;
        try {
            try {
                await _private.http.post(`${setup.base}/${setup.id}/appearance`, { darkMode, brand, useCaseName });
            } catch (error) {
                if (error.status !== 409 || !isCurrent(setup)) throw error;
                // A helper report can advance the same session revision. Retry once.
                await _private.http.post(`${setup.base}/${setup.id}/appearance`, { darkMode, brand, useCaseName });
            }
            setup.lastDarkMode = darkMode;
            setup.lastBrand = brand;
            setup.lastUseCaseName = useCaseName;
        } catch {
            // Appearance is optional. A later setup action retries the latest mode.
        }
    });
    return setup.appearanceUpdate;
}
appearance.onChange(() => syncAppearance());
session.onSessionChange((type, next) => {
    if (type !== 'destroySession' && !(current && next?.tenant && scopeFor(next) !== current.scope)) return;
    clear();
    // Login redirects and cache resets can bypass the form's logout rule.
    _private.forgetHelper().catch(() => logging.warn('Unable to disconnect Reader Connector.'));
});
const scopeFor = value => `${value.groupNumber || 0}:${value.tenant?.['identity:tenant']?._id}`;
export function state(context) {
    const scope = scopeFor(context.session);
    const userId = context.session.briefUserReference?._id;
    if (current?.scope !== scope || current?.userId !== userId) {
        const previous = saved();
        current = {
            scope,
            userId,
            base: `/g/${context.session.groupNumber || 0}/api/deviceManagement/device/setup`,
            draft: {
                _id: new ObjectId().toString(),
                paired: false,
                needsRegion: false,
                advancedRegion: false,
                readyToConnect: false,
                hasReadersSelected: false,
                manualAddress: false,
                readerUsername: 'admin',
                mqttUsername: context.session.tenant?.['identity:tenant']?._id
            },
            pending: false
        };
        if (previous?.scope === scope && previous.userId === userId) {
            current.id = previous.id;
            current.mqttLoginReady = previous.mqttLoginReady;
        } else forget();
    }
    current.useCaseName = metadata.getUseCaseTitle(uiRuntimeConfig.getBrand(), context.session) || 'your app';
    return current;
}
export function hasCurrentReview(reader) {
    return reader?.report?.status === 'review' && new Date(reader.report.plan?.expiresAt) > new Date();
}
function readerStatus(reader) {
    if (!reader) return 'Discovered';
    if (reader.setupStatus === 'Ready') return 'Connected';
    if (reader.setupStatus?.startsWith('Needs attention:')) return reader.setupStatus;
    if (reader.report?.status === 'needs-attention') return 'Needs attention';
    if (reader.action !== 'apply' && reader.setupStatus !== 'Ready' && reader.report?.status === 'review')
        return hasCurrentReview(reader) ? 'Awaiting confirmation' : 'Needs attention: Settings expired. Check this reader again.';
    return reader.action === 'apply' ? 'Provisioning' : 'Checking';
}
export function normalizeAddress(address) {
    if (!address) return '';
    try {
        return readerAddress(address).host;
    } catch {
        // Preserve incomplete form input; the helper validates it before connecting.
        return address;
    }
}
export function selectedAddresses(draft) {
    const choices = draft.readerChoices?.map(row => row._id) || [];
    return [
        ...new Set(
            [...choices, ...(draft.readerAddress && !choices.length ? [draft.readerAddress] : [])].map(normalizeAddress)
        )
    ];
}
export function display(draft, snapshot) {
    const addresses = selectedAddresses(draft);
    const visibleAddresses = [
        ...new Set([
            ...(draft.candidates || []).map(row => normalizeAddress(row._id)),
            ...(draft.readerProgress || []).map(row => normalizeAddress(row._id)),
            ...addresses,
            ...snapshot.readers
                .map(row => normalizeAddress(row.report?.address))
                .filter(Boolean)
        ])
    ];
    const readerProgress = visibleAddresses.map(address => {
        const row = snapshot.readers.find(
            item => normalizeAddress(item.report?.address) === address
        );
        const status = readerStatus(row);
        const error =
            draft.reviewErrors?.[address] ||
            row?.report?.error ||
            (status?.startsWith('Needs attention:') ? status.slice('Needs attention:'.length).trim() : '');
        const displayStatus = error ? 'Needs attention' : normalizeAddress(draft.checkingAddress) === address ? 'Checking' : status;
        return {
            _id: address,
            title: (
                row?.name ||
                draft.candidates?.find(item => normalizeAddress(item._id) === address)?.hostname ||
                draft.readerProgress?.find(item => normalizeAddress(item._id) === address)?.title ||
                address
            ).toLowerCase(),
            address,
            registration:
                row?.registration ||
                draft.candidates?.find(item => normalizeAddress(item._id) === address)?.registration ||
                'Unknown',
            status: displayStatus,
            statusColor: {
                Discovered: 'Cool Gray',
                Checking: 'Blue',
                'Awaiting confirmation': 'Yellow',
                Provisioning: 'Blue',
                Connected: 'Green',
                'Needs attention': 'Red'
            }[displayStatus],
            details: error || (status === 'Awaiting confirmation' && row?.report?.selectedRegion
                ? `Country/region will be set to ${row.report.selectedRegion.regionName}.`
                : '')
        };
    });
    const selectedProgress = readerProgress.filter(row => addresses.includes(row._id));
    const reader =
        snapshot.readers.find(
            row =>
                draft.readerAddress &&
                normalizeAddress(row.report?.address) === normalizeAddress(draft.readerAddress)
        ) ||
        snapshot.readers.find(row => row.name === draft.readerName) ||
        (!draft.readerName && !draft.readerAddress && snapshot.readers.length === 1 ? snapshot.readers[0] : undefined);
    const plan = reader?.report?.plan;
    return presentation({
        ...draft,
        paired: !!snapshot.paired,
        readerProgress,
        hasReadersSelected: addresses.length > 0,
        completedReaders: readerProgress.filter(row => row.status === 'Connected').length,
        batchProgress: progressSummary(readerProgress),
        countries: (reader?.report?.supportedRegions || []).map(name => ({ _id: name, title: name })),
        readyToConnect: selectedProgress.some(row => row.status === 'Awaiting confirmation'),
        connectionStatus: snapshot.paired ? 'Connector connected' : 'Waiting for Reader Connector',
        readerSummary:
            snapshot.readers.map(row => `${row.name?.toLowerCase()}: ${readerStatus(row)}`).join('; ') || 'No readers inspected yet.',
        settingsSummary: plan
            ? `Connect ${reader.name.toLowerCase()} to ${snapshot.connection?.tenantTitle || 'this tenant'} on ${plan.endpoint?.hostname}.${plan.region ? ` ${plan.region}` : ''} The reader app is managed automatically.`
            : 'Select the readers you want to provision.',
        selectedRegion: reader?.report?.selectedRegion
            ? `${reader.report.selectedRegion.regionName} · ${reader.report.selectedRegion.standardName}`
            : 'Keep the current country / region',
        supportedRegions: reader?.report?.supportedRegions?.join('; ') || '',
        currentRegion: reader?.report?.region
            ? `${reader.report.region.regionName} · ${reader.report.region.standardName}`
            : '',
        regionName:
            draft.regionName ||
            (reader?.report?.supportedRegions?.length === 1 ? reader.report.supportedRegions[0] : ''),
        readerName: reader?.name || draft.readerName,
        readerStatus: readerStatus(reader),
        readerError: reader?.report?.error || '',
        operationId: reader?.operationId,
        readerId: reader?.id
    });
}

// Keep button availability and credential visibility consistent after selection,
// status updates and failures. These flags drive existing metadata controls.
export function presentation(draft) {
    const addresses = selectedAddresses(draft);
    const selected = (draft.readerProgress || []).filter(row => addresses.includes(normalizeAddress(row._id)));
    const checkCount = addresses.filter(
        address =>
            !selected.some(
                row =>
                    normalizeAddress(row._id) === address &&
                    ['Connected', 'Awaiting confirmation', 'Provisioning'].includes(row.status)
            )
    ).length;
    const needsCheck = checkCount > 0;
    const readyCount = selected.filter(row => row.status === 'Awaiting confirmation').length;
    const failed = (draft.readerProgress || []).filter(row => row.status === 'Needs attention');
    const batch = (draft.readerProgress || []).filter(
        row => draft.provisioningAddresses?.includes(row._id) || row.status === 'Provisioning'
    );
    const finished = batch.filter(row => ['Connected', 'Needs attention'].includes(row.status)).length;
    const provisioning = batch.some(row => row.status === 'Provisioning');
    // A later status read can finish a batch after the initial wait has ended.
    // Replace the saved progress message once every reader has a final result.
    const requestStatus = batch.length && finished === batch.length && draft.requestStatus?.startsWith('Provisioning readers')
        ? progressSummary(batch)
        : draft.requestStatus;
    const busy = !!draft.busy || provisioning;
    const showCredentials = !!draft.paired && needsCheck && !!draft.credentialsRequested;
    return {
        ...draft,
        showDownloads: !draft.paired && typeof connector.download === 'function',
        showRequestStatus: !!requestStatus || provisioning,
        requestStatus: provisioning
            ? `Provisioning readers… ${finished} of ${batch.length} finished. This may take a few minutes. ${connector.keepOpenMessage}`
            : requestStatus,
        operationBusy: busy,
        showDiscoveryIntro: !busy && !draft.readerProgress?.length && !showCredentials && !draft.manualAddress,
        provisionSelectedLabel: addresses.length
            ? `Provision ${addresses.length} reader${addresses.length === 1 ? '' : 's'}`
            : 'Provision',
        checkLabel: `Check ${checkCount} reader${checkCount === 1 ? '' : 's'}`,
        provisionLabel: `Provision ${readyCount} reader${readyCount === 1 ? '' : 's'}`,
        retryLabel: `Retry ${failed.length} reader${failed.length === 1 ? '' : 's'}`,
        canRetry: !!draft.paired && !busy && !showCredentials && failed.length > 0 && !readyCount,
        showCompletion: !busy && draft.completedReaders > 0,
        manualAddress: draft.manualAddress === true,
        canFindReaders: !busy,
        findReadersLabel: draft.paired ? 'Find readers' : 'Open Reader Connector and find readers',
        canProvision: !!draft.paired && !busy && needsCheck && !showCredentials && !readyCount &&
            !(selected.length === addresses.length && selected.every(row => row.status === 'Needs attention')),
        canContinue: showCredentials && !busy,
        canConfirm: !!draft.paired && !busy && selected.some(row => row.status === 'Awaiting confirmation'),
        showCredentials,
        showMqttPassword: showCredentials && !draft.mqttLoginReady
    };
}
function progressSummary(rows) {
    return [
        ['Connected', 'connected'],
        ['Awaiting confirmation', 'awaiting confirmation'],
        ['Provisioning', 'provisioning'],
        ['Needs attention', 'need attention']
    ]
        .map(([status, label]) => {
            const count = rows.filter(row => row.status === status).length;
            return count ? `${count} ${label}` : '';
        })
        .filter(Boolean)
        .join(' · ');
}
